import { useCallback, useEffect, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Check,
  ChevronDown,
  ClipboardCheck,
  Gauge,
  Link as LinkIcon,
  Loader2,
  Ban,
  Wallet,
  RefreshCw,
  SlidersHorizontal,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';

import { isAxiosError } from 'axios';

import { SOFT_FLOOR_DAILY_CAP, isCapDirty, parseCapDraft } from '../../config/dailyCap';
import {
  DEFAULT_VERIFICATION_ATTEMPTS,
  MAX_VERIFICATION_ATTEMPTS,
  MIN_VERIFICATION_ATTEMPTS,
  retryWindowMinutes,
} from '../../config/retrySchedule';
import { documentCaptureService } from '../../services/documentCaptureService';
import type {
  VerificationStats,
  VerifierConfig,
  VerifierConfigUpdate,
} from '../../types/documentCapture.types';

/**
 * "Second reader" admin surface (86d3x48q7 T13, bundle §4.6). Gated on `docscan.ai_manage` at the
 * call site — this component assumes the caller already checked.
 *
 * Two tabs, two different jobs: **Settings** changes how identification behaves for everyone, and
 * **Calibration** reports what the window has measured so far.
 *
 * ## Copy rules that are not negotiable here
 *
 * - **"Verified" appears nowhere.** The AI is being measured, not trusted.
 * - **Model names live behind a disclosure.** The operator picks a LEVEL; a level runs a primary
 *   model and falls back automatically. Putting model names in their face invites tuning a thing
 *   that is only meaningful as a pair.
 * - **The honesty card is mandatory** on Calibration. Human resolutions are ~4% wrong on the
 *   calibration corpus — the same order as the effects being measured — so these are directions,
 *   not settled rates.
 * - **Accuracy excludes auto-linked rows.** For those, "correct" is whatever the gate chose, so
 *   scoring against them is circular.
 */
/**
 * 86d3ygnz3 — pull the validator's field message out of a failed save.
 *
 * The API answers a FluentValidation failure with `ApiResponse.errors` keyed by field (400). That is a
 * permanent input error, so it must be rendered where the input is — the previous blanket "try again
 * in a moment" invited people to retry an unchanged, still-rejected value. Anything without that shape
 * is NOT a validation failure and is handled by re-reading the server's state instead.
 */
function validationMessage(err: unknown): string | null {
  if (!isAxiosError(err) || err.response?.status !== 400) return null;
  const body = err.response.data as
    | { message?: string; errors?: Record<string, string[]> }
    | undefined;
  const first = Object.values(body?.errors ?? {})
    .flat()
    .find((m) => typeof m === 'string' && m.length > 0);
  return first ?? body?.message ?? 'That value was not accepted.';
}

interface SecondReaderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SecondReaderDialog({ open, onOpenChange }: SecondReaderDialogProps) {
  const [config, setConfig] = useState<VerifierConfig | null>(null);
  const [stats, setStats] = useState<VerificationStats | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [saving, setSaving] = useState(false);

  // Draft mirrors the three editable fields. Plain values, compared directly — NOT a store getter
  // called in render, which the React Compiler memoizes on the stable function reference and then
  // serves stale (CLAUDE.md 86d3d1z40; the Save-stuck-disabled bug was exactly this shape).
  const [enabled, setEnabled] = useState(false);
  const [level, setLevel] = useState<'standard' | 'careful'>('standard');
  const [spotCheck, setSpotCheck] = useState(10);
  const [maxAttempts, setMaxAttempts] = useState(DEFAULT_VERIFICATION_ATTEMPTS);
  // ⚠️ STRING draft, deliberately — see config/dailyCap.ts. A numeric state behind this input turns a
  // cleared field into cap=0, which stands the reader down entirely.
  const [capDraft, setCapDraft] = useState('');
  const [showModels, setShowModels] = useState(false);
  /** Field-level message from a 400, rendered under the input it belongs to. */
  const [capServerError, setCapServerError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState('loading');
    try {
      // Parallel, not sequential: the two reads are independent and the modal should not paint
      // twice on open.
      const [c, s] = await Promise.all([
        documentCaptureService.getVerifierConfig(),
        documentCaptureService.getVerificationStats(),
      ]);
      setConfig(c);
      setStats(s);
      setEnabled(c.enabled);
      setLevel(c.level);
      setSpotCheck(c.spotCheckRatePercent);
      setMaxAttempts(c.maxVerificationAttempts);
      setCapDraft(String(c.dailyCallCap));
      setCapServerError(null);
      setState('ready');
    } catch {
      setState('error');
    }
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  // Parsed ONCE per render; everything below reads the result. Never re-parse at a call site — that
  // is how two places end up disagreeing about whether the draft is valid.
  const cap = parseCapDraft(capDraft);
  const capDirty = config != null && isCapDirty(cap, config.dailyCallCap);

  const dirty =
    config != null &&
    (enabled !== config.enabled ||
      level !== config.level ||
      spotCheck !== config.spotCheckRatePercent ||
      maxAttempts !== config.maxVerificationAttempts ||
      capDirty);

  // An invalid draft blocks Save outright, even when the change the admin wants is elsewhere in the
  // dialog. Saving "the rest of it" while a half-typed cap sits in the box would leave them looking at
  // a number that is not what is running.
  const blocked = !cap.ok;

  const save = useCallback(async () => {
    if (!config) return;
    setSaving(true);
    // PARTIAL update — only what changed. A full-object PUT from a stale form would silently
    // overwrite another admin's change; there is no row versioning anywhere in this codebase.
    const update: VerifierConfigUpdate = {};
    if (enabled !== config.enabled) update.enabled = enabled;
    if (level !== config.level) update.level = level;
    if (spotCheck !== config.spotCheckRatePercent) update.spotCheckRatePercent = spotCheck;
    if (maxAttempts !== config.maxVerificationAttempts) update.maxVerificationAttempts = maxAttempts;
    // ONLY when actually edited AND parseable. An untouched field must be ABSENT from the body, not
    // echoed: with no row versioning anywhere, that absence is the single thing stopping a dialog
    // opened before an incident from reverting a colleague's mid-incident raise.
    if (capDirty && cap.ok) update.dailyCallCap = cap.value;
    try {
      const saved = await documentCaptureService.updateVerifierConfig(update);
      // Re-baseline from what the server returned, not from the draft — that is what makes the
      // dirty check honest if the server clamped or normalised anything.
      setConfig(saved);
      setEnabled(saved.enabled);
      setLevel(saved.level);
      setSpotCheck(saved.spotCheckRatePercent);
      setMaxAttempts(saved.maxVerificationAttempts);
      setCapDraft(String(saved.dailyCallCap));
      setCapServerError(null);

      // 86d3ygnz3 — refetch the counters. `stats` is loaded once on open and the cap row derives
      // "Reached" from it, so without this a successful raise leaves the row still showing the old cap
      // and still saying Reached, directly under a success toast. Mid-incident that reads as "the fix
      // did not take" and invites a second, larger raise.
      // Best-effort: the save itself already succeeded, and failing the whole action over a stale
      // counter would be a worse lie than the stale counter.
      try {
        setStats(await documentCaptureService.getVerificationStats());
      } catch {
        /* counters stay as they were; the cap row still reads the fresh config value */
      }

      toast.success('Second reader settings saved');
    } catch (err) {
      // A 400 is the validator, and it is never fixed by waiting: show WHICH field and why, where the
      // field is. Telling someone to "try again in a moment" after a permanent input error is how they
      // end up retrying the same rejected value.
      const field = validationMessage(err);
      if (field) {
        setCapServerError(field);
        toast.error('Those settings weren’t accepted — see the message on the field.');
      } else {
        // Anything else and we no longer know what the server holds. Re-read before allowing another
        // attempt, so a retry can never blind-push a draft built on a stale baseline.
        toast.error('Couldn’t save those settings. Reloading the current values…');
        await load();
      }
    } finally {
      setSaving(false);
    }
  }, [config, enabled, level, spotCheck, maxAttempts, capDirty, cap, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        The tab strip lives INSIDE the header row, right-aligned — so `Tabs` has to wrap the header
        too. The full-width two-column `TabsList` this used to have read as a page-level mode switch
        for a 700px dialog; the prototype's is a 30px segmented control that sits beside the title
        and stays out of the way of the settings it is switching between.
      */}
      <DialogContent className="flex max-h-[88vh] max-w-[700px] flex-col gap-0 overflow-hidden rounded-2xl p-0">
        <Tabs defaultValue="settings" className="flex min-h-0 flex-1 flex-col">
          <DialogHeader className="flex-shrink-0 flex-row items-center gap-[11px] space-y-0 border-b border-border px-[18px] py-3.5 pr-12">
            <span
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[9px] bg-info/12 text-info"
              aria-hidden
            >
              <Sparkles className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-[14.5px] font-bold">Second reader</DialogTitle>
              {/* The permission is named on purpose: it answers "why can I see this and my colleague
                  can't" without anyone having to ask. */}
              <p className="text-[11.5px] text-muted-foreground">
                An independent reading of every scan, measured against the barcode reader ·{' '}
                <span className="font-mono">docscan.ai_manage</span>
              </p>
            </div>
            {state === 'ready' && (
              <TabsList className="h-[30px] flex-shrink-0 gap-0.5 rounded-lg bg-muted p-[3px]">
                <TabsTrigger
                  value="settings"
                  className="h-6 gap-1.5 rounded-md px-2.5 text-[11.5px] font-semibold"
                >
                  <SlidersHorizontal className="h-3 w-3" aria-hidden />
                  Settings
                </TabsTrigger>
                <TabsTrigger
                  value="calibration"
                  className="h-6 gap-1.5 rounded-md px-2.5 text-[11.5px] font-semibold"
                >
                  <BarChart3 className="h-3 w-3" aria-hidden />
                  Calibration
                </TabsTrigger>
              </TabsList>
            )}
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-[18px] py-4">
            {state === 'loading' && (
              <div className="space-y-3">
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            )}

            {state === 'error' && (
              <div className="space-y-3 py-4 text-center">
                <p className="text-sm text-muted-foreground">Couldn’t load the second reader settings.</p>
                <Button variant="outline" size="sm" onClick={() => void load()}>
                  Try again
                </Button>
              </div>
            )}

            {state === 'ready' && config && stats && (
              <>
                <TabsContent value="settings" className="mt-0 space-y-5">
                  <MasterToggle enabled={enabled} onChange={setEnabled} />
                  <LevelCards
                    level={level}
                    onChange={setLevel}
                    config={config}
                    showModels={showModels}
                    onToggleModels={() => setShowModels((v) => !v)}
                  />
                  <SpotCheckControl value={spotCheck} onChange={setSpotCheck} autoLinkedToday={stats.autoLinkedToday} />
                  <RetryCountControl value={maxAttempts} onChange={setMaxAttempts} />
                  <DailyCapControl
                    draft={capDraft}
                    onChange={(v) => {
                      setCapDraft(v);
                      setCapServerError(null);   // the admin is fixing it; stop showing the old rejection
                    }}
                    parsed={cap}
                    serverError={capServerError}
                    callsToday={stats.callsToday}
                    savedCap={config.dailyCallCap}
                  />
                  {/* ⚠️ The cap comes from CONFIG (just re-baselined by the save), the counters from
                      STATS (refetched on save, best-effort). Mixing the two the other way round is the
                      stale-row bug: a raise would leave the row quoting the old ceiling. */}
                  <TodaysChecks stats={stats} cap={config.dailyCallCap} />
                  <BothBusyExplainer />
                </TabsContent>

                <TabsContent value="calibration" className="mt-0 space-y-4">
                  <CalibrationTab stats={stats} />
                </TabsContent>
              </>
            )}
          </div>

          {state === 'ready' && (
            <div className="flex flex-shrink-0 justify-end gap-2 border-t border-border px-[18px] py-3">
              <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={saving}>
                Close
              </Button>
              <Button size="sm" onClick={() => void save()} disabled={!dirty || saving || blocked}>
                {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />}
                Save changes
              </Button>
            </div>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- settings

function MasterToggle({ enabled, onChange }: { enabled: boolean; onChange: (v: boolean) => void }) {
  return (
    <section className="space-y-2">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <label htmlFor="verifier-enabled" className="block text-sm font-medium text-foreground">
            Read every scan a second time
          </label>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {enabled ? 'On — every scan gets a second reading' : 'Off — the barcode reader links alone'}
          </p>
        </div>
        <Switch id="verifier-enabled" checked={enabled} onCheckedChange={onChange} />
      </div>

      {/*
        ⚠️ OFF is NOT "nothing links". Owner decision 2026-08-04: with the second reader off, the
        pipeline falls back to decoder-alone auto-linking — the pre-verifier behaviour, now with
        repair behind it. An earlier draft of this card said nothing auto-links while off, which
        would have described a system that does not exist and made "off" look safer than it is.
      */}
      {!enabled && (
        <div className="rounded-lg border border-warning/30 bg-warning/10 p-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning" aria-hidden />
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">Linking on the barcode reader alone</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                While this is off, a clean read from the barcode reader links by itself — no second reading, no
                agreement check. The barcode reader got 27 out of 27 right on the test set, but
                nothing double-checks it while this is off. Scans it can’t read still go to review.
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

const LEVELS = [
  {
    value: 'standard' as const,
    name: 'Standard',
    blurb:
      'Fast and cheap. Enough to read a printed number off a clean scan — the right default for every scan, all day.',
    speed: 3,
    cost: 1,
  },
  {
    value: 'careful' as const,
    name: 'Careful',
    blurb:
      'Slower and costs more, but better on faint or crooked paper. Worth switching to if the results below show Standard missing numbers the barcode reader missed too.',
    speed: 1,
    cost: 3,
  },
];

function LevelCards({
  level,
  onChange,
  config,
  showModels,
  onToggleModels,
}: {
  level: 'standard' | 'careful';
  onChange: (v: 'standard' | 'careful') => void;
  config: VerifierConfig;
  showModels: boolean;
  onToggleModels: () => void;
}) {
  const careful = level === 'careful';
  return (
    <section className="space-y-2">
      <h3 className="flex items-center gap-1.5 text-[13px] font-semibold text-foreground">
        <Gauge className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" aria-hidden />
        Reader level
      </h3>
      <p className="text-xs leading-relaxed text-muted-foreground">
        Each level runs a <strong className="font-medium text-foreground">primary</strong> model and falls
        back automatically if it’s busy. Pick the behaviour you want; the models behind it are an
        implementation detail.
      </p>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {LEVELS.map((l) => {
          const active = level === l.value;
          return (
            <button
              key={l.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(l.value)}
              className={cn(
                'rounded-lg border p-3 text-left transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                // Selected = tinted fill, not a ring. A ring on white next to white made the two
                // cards read as equally available; the fill says which one is running.
                active ? 'border-primary bg-primary/[0.06]' : 'border-border hover:bg-accent/40'
              )}
            >
              <span className="flex items-center gap-2">
                <span
                  className={cn(
                    'flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-full',
                    active ? 'bg-primary' : 'border border-muted-foreground/50'
                  )}
                  aria-hidden
                />
                <span className="sr-only">{active ? 'Selected' : 'Not selected'}</span>
                <span className="text-sm font-medium text-foreground">{l.name}</span>
              </span>
              <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{l.blurb}</p>
              <div className="mt-2 space-y-1">
                <Meter label="Speed" value={l.speed} />
                <Meter label="Cost" value={l.cost} />
              </div>
            </button>
          );
        })}
      </div>

      {/*
        Model names behind a disclosure, per the Extraction Accuracy precedent. They are diagnostic
        detail, not a control: the level→model map lives server-side and the client never sends a
        model name.
      */}
      <button
        type="button"
        onClick={onToggleModels}
        aria-expanded={showModels}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <ChevronDown className={cn('h-3 w-3 transition-transform', showModels && 'rotate-180')} aria-hidden />
        Show model names
      </button>
      {showModels && (
        <div className="space-y-1 rounded-lg border border-border bg-muted/40 p-3 text-xs">
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">Primary</span>
            <span className="font-mono text-foreground">
              {careful ? config.carefulPrimaryModel : config.standardPrimaryModel}
            </span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground">Busy-fallback</span>
            <span className="font-mono text-foreground">
              {careful ? config.carefulFallbackModel : config.standardFallbackModel}
            </span>
          </div>
          <p className="pt-1 leading-relaxed text-muted-foreground">
            Document Capture keeps its own pair, separate from the quotation extractor — a model change
            there must not silently re-shape the calibration sample here.
          </p>
        </div>
      )}
    </section>
  );
}

function Meter({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-10 text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="flex gap-1" role="img" aria-label={`${label} ${value} of 3`}>
        {[1, 2, 3].map((i) => (
          <span
            key={i}
            className={cn('h-1 w-4 rounded-full', i <= value ? 'bg-primary' : 'bg-muted')}
            aria-hidden
          />
        ))}
      </span>
    </div>
  );
}

function SpotCheckControl({
  value,
  onChange,
  autoLinkedToday,
}: {
  value: number;
  onChange: (v: number) => void;
  autoLinkedToday: number;
}) {
  const sampled = Math.round((autoLinkedToday * value) / 100);
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-4">
        <label htmlFor="spot-check-rate" className="flex items-center gap-1.5 text-[13px] font-semibold text-foreground">
          <ClipboardCheck className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" aria-hidden />
          Spot-check auto-linked scans
        </label>
        <span className="text-[13px] font-bold tabular-nums text-foreground">{value}%</span>
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        When both readers agree, the scan links with nobody looking. That is assumed safe. This is the
        only thing that measures it: a random share of agreements comes back to a person, so agreement
        accuracy is a number instead of a hope.
      </p>
      <Slider id="spot-check-rate" value={value} onValueChange={onChange} min={0} max={25} step={1} />
      {/* The ends of the range, said out loud. A bare track gives no clue that 25% is the ceiling —
          an operator dragging right has no idea whether they are near it. */}
      <div className="flex justify-between text-[10.5px] text-muted-foreground">
        <span>Off</span>
        <span>25%</span>
      </div>
      {value === 0 ? (
        <p className="text-xs leading-relaxed text-warning">
          No sampling — auto-link accuracy goes unmeasured, and the end-of-window decision rests on an
          assumption.
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          About {sampled} of today’s {autoLinkedToday} auto-linked scans would come back to a person.
        </p>
      )}
    </section>
  );
}

/**
 * 86d3yeh4j — how many times ONE scan's second reading is tried before a person has to look at it.
 *
 * ## The echo is the point, and it is COMPUTED
 *
 * A bare "4" is untunable: nobody knows whether that is ten seconds of patience or an hour of it. So
 * the control prints the wait — and the moment it prints a duration, that duration can be wrong. The
 * approved copy said "about 20 minutes" at four tries, from a 1/4/7/10-minute schedule read as four
 * waits. Four tries is four READS and therefore three waits (1+4+7 = 12), so the number overstated
 * the wait by 67%. It is derived from `retrySchedule.ts` here, which mirrors the server's own array,
 * so it cannot drift from what actually happens — see that file.
 *
 * ## What the count does and does not govern
 *
 * TRANSIENT failures only. A scan too large to send forfeits its remaining tries (retrying cannot
 * change its bytes), and an outage spends none of them at all (the scans are held instead). That is
 * what makes the promise below true: reaching a person means every configured try really did fail on
 * THIS scan.
 */
function RetryCountControl({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const minutes = retryWindowMinutes(value);
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-4">
        <label
          htmlFor="verify-attempts"
          className="flex items-center gap-1.5 text-[13px] font-semibold text-foreground"
        >
          <RefreshCw className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" aria-hidden />
          Tries before a person looks at it
        </label>
        <span className="text-[13px] font-bold tabular-nums text-foreground">{value}</span>
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        A dropped connection or a busy model tells us nothing about the scan itself. DOP simply tries
        again, waiting a little longer each time, and only gives up once every try has failed.
      </p>
      <Slider
        id="verify-attempts"
        value={value}
        onValueChange={onChange}
        min={MIN_VERIFICATION_ATTEMPTS}
        max={MAX_VERIFICATION_ATTEMPTS}
        step={1}
      />
      {/* The ends said out loud — a bare track gives no clue where the range stops. */}
      <div className="flex justify-between text-[10.5px] text-muted-foreground">
        <span>{MIN_VERIFICATION_ATTEMPTS} — no retry</span>
        <span>{MAX_VERIFICATION_ATTEMPTS}</span>
      </div>
      {value === MIN_VERIFICATION_ATTEMPTS ? (
        <p className="text-xs leading-relaxed text-warning">
          No trying again — one dropped connection is enough to put the scan in front of a person.
          {' '}{DEFAULT_VERIFICATION_ATTEMPTS} is the normal setting.
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          Spread over about {minutes} minute{minutes === 1 ? '' : 's'}. A scan only reaches a person if
          every try fails.
        </p>
      )}
    </section>
  );
}

/**
 * 86d3ygnz3 — the daily call cap, editable.
 *
 * This control exists because the cap is the lever people reach for DURING an incident, and until now
 * reaching for it meant hand-crafting a `PUT admin/verifier-config` — which is exactly how production
 * moved 500 → 1000 → 2000 in one afternoon.
 *
 * ## What the copy has to get right
 *
 * Running out of budget no longer sends scans to a person. Since 86d3yeh4j they are HELD and go
 * through at the reset, or the moment the cap is raised. Every sentence here says that, because the
 * decision an admin makes with this number depends entirely on which of the two is true: a review
 * flood is urgent and irreversible, a queue of waiting scans is neither.
 *
 * ## The reset is stated in two ways on purpose
 *
 * The budget rolls on the UTC day, which is 8:00 AM in Manila — not midnight, and not the end of the
 * working day. It also resets whenever the API restarts, because the counter is in-process. An admin
 * deciding whether to raise the cap at 4 PM needs both facts; only one of them is a clock.
 */
function DailyCapControl({
  draft,
  onChange,
  parsed,
  serverError,
  callsToday,
  savedCap,
}: {
  draft: string;
  onChange: (v: string) => void;
  parsed: ReturnType<typeof parseCapDraft>;
  serverError: string | null;
  callsToday: number;
  savedCap: number;
}) {
  const value = parsed.ok ? parsed.value : null;
  const belowConsumed = value != null && value < callsToday;
  const softFloor = value != null && value > 0 && value < SOFT_FLOOR_DAILY_CAP;

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-4">
        {/* A real text label, not a placeholder — the number means nothing without it, and a
            placeholder disappears the moment anyone types. */}
        <label htmlFor="daily-cap" className="flex items-center gap-1.5 text-[13px] font-semibold text-foreground">
          <Wallet className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" aria-hidden />
          Second readings per day
        </label>
        <Input
          id="daily-cap"
          // `inputMode` gives phones the numeric keypad WITHOUT type="number", whose badInput state
          // reports an empty string for "12e" and would hand us a silent 0. See config/dailyCap.ts.
          inputMode="numeric"
          autoComplete="off"
          value={draft}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={!parsed.ok || serverError != null}
          aria-describedby="daily-cap-help"
          className="h-8 w-28 text-right tabular-nums"
        />
      </div>

      <p className="text-xs leading-relaxed text-muted-foreground">
        A limit on cost, not on capture. Past this number scans are not read a second time — they wait,
        and go through on their own at the reset. Nothing is lost, and nothing lands in the review queue
        because of it.
      </p>

      <div id="daily-cap-help" className="space-y-1">
        {!parsed.ok ? (
          <p className="text-xs leading-relaxed text-destructive">{parsed.message}</p>
        ) : serverError ? (
          <p className="text-xs leading-relaxed text-destructive">{serverError}</p>
        ) : (
          <p className="text-xs text-muted-foreground tabular-nums">
            Used {callsToday} of {savedCap} today — starts again at 8:00 AM (Manila), and if the
            system restarts.
          </p>
        )}

        {/* Ordered by how much trouble each one causes, most first. */}
        {value === 0 && <CapStandDownWarning />}

        {belowConsumed && value !== 0 && (
          <p className="text-xs leading-relaxed text-warning">
            You’ve already used {callsToday} today; saving {value} stops second readings immediately.
            Scans wait for the reset rather than going to review — raising it again releases them.
          </p>
        )}

        {softFloor && !belowConsumed && (
          <p className="text-xs leading-relaxed text-warning">
            A number this small can be used up by retries alone on a bad day — every try counts
            against it, including the ones that come back with nothing.
          </p>
        )}

        {/* Stated wherever the cap is raised, because it is the one thing a raise CANNOT do. */}
        <p className="text-xs leading-relaxed text-muted-foreground">
          A raised cap applies to scans not yet checked, and releases any that are waiting. Scans
          already sent to review stay there.
        </p>
      </div>
    </section>
  );
}

/**
 * cap = 0 is legal, occasionally deliberate, and a foot-gun — so it is never silent.
 *
 * ⚠️ Its consequence CHANGED with 86d3yeh4j and is now worse, not better. It used to mean "every scan
 * goes to review unchecked", which is loud and self-announcing. It now means every scan WAITS —
 * indefinitely, because the release condition is `used < cap` and nothing is ever below zero. The
 * pipeline stops identifying anything and the only outward sign is rows that quietly stop moving.
 * Whoever types a zero has to be told that, in those words.
 */
function CapStandDownWarning() {
  return (
    <div className="rounded-lg border border-warning/30 bg-warning/10 p-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning" aria-hidden />
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">0 stops every second reading</p>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            No scan is read a second time, and none of them go to review either — they wait, and they
            keep waiting, because a limit of zero never has room again. New versions of existing
            documents stop going live too. Nothing moves until somebody raises this number.
          </p>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            To go back to linking on the barcode reader alone, turn the second reader off at the top of this dialog
            instead — that keeps captures flowing.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Today's checks — the prototype's card chrome (bordered, tinted header, one tile+count per row).
 *
 * ⚠️ The prototype's FOUR rows are mock: "read by the primary model", "fell back to the second
 * model", "deferred and retried", "gave up after retries". `GET verification/stats` returns no
 * per-outcome breakdown, so those rows cannot be computed — and inventing four plausible numbers on
 * an admin screen whose entire purpose is measuring how much to trust a reader would be worse than
 * any layout mismatch. These three are what the endpoint actually knows. Adding the other four is a
 * server change (attempt outcomes are on `DocumentIdentificationAttempts`), not a UI one.
 */
const CHECK_TONES = {
  success: 'bg-success/12 text-success',
  info: 'bg-info/12 text-info',
  // 86d3ygnz3 — a spent budget is not neutral information. It is the state in which scans stop being
  // checked, and it rendered as `info` — the same tone as "auto-linked" — while 234 scans piled up.
  warning: 'bg-warning/12 text-warning',
  muted: 'bg-muted text-muted-foreground',
} as const;

function CheckRow({
  icon: Icon,
  tone,
  title,
  detail,
  value,
}: {
  icon: typeof Check;
  tone: keyof typeof CHECK_TONES;
  title: string;
  detail: string;
  value: number;
}) {
  return (
    <div className="flex items-center gap-2.5 border-t border-border/60 px-[13px] py-2.5">
      <span
        className={cn('flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-[7px]', CHECK_TONES[tone])}
        aria-hidden
      >
        <Icon className="h-3 w-3" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold text-foreground">{title}</div>
        <div className="text-[11px] text-muted-foreground">{detail}</div>
      </div>
      <span className="flex-shrink-0 text-sm font-bold tabular-nums text-foreground">{value}</span>
    </div>
  );
}

/**
 * 86d3ygnz3 — WHICH NUMBER COMES FROM WHERE, because mixing them is the stale-row bug:
 *
 * - `cap` is passed in from the just-re-baselined CONFIG, so a raise shows the instant the save
 *   returns. Reading `stats.dailyCallCap` here instead left the row quoting the OLD ceiling — and
 *   still saying "Reached" — directly beneath a success toast.
 * - the counters (`callsToday`, `deniedToday`, `autoLinkedToday`) come from STATS, refetched
 *   best-effort after a save. They are in-process server counters, not database facts.
 */
function TodaysChecks({ stats, cap }: { stats: VerificationStats; cap: number }) {
  // `>=` alone is TRUE at 0 of 0, announcing "Reached" for a cap nothing ever approached — the state
  // a stood-down reader sits in permanently. A cap is only reached if there was one to reach.
  const capped = cap > 0 && stats.callsToday >= cap;
  const standDown = cap === 0;
  return (
    <section className="overflow-hidden rounded-[11px] border border-border">
      <div className="flex items-center gap-[7px] border-b border-border bg-muted/30 px-[13px] py-2.5">
        <Activity className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" aria-hidden />
        <h3 className="text-[12.5px] font-semibold text-foreground">Today’s checks</h3>
      </div>
      <CheckRow
        icon={Check}
        tone="success"
        title="Second readings"
        detail="Scans read a second time today"
        value={stats.callsToday}
      />
      <CheckRow
        icon={Gauge}
        tone={capped || standDown ? 'warning' : 'muted'}
        title="Daily cap"
        // 86d3yeh4j/86d3ygnz3 — the original sentence ("scans link on the barcode alone") was false
        // BEFORE either ticket: a capped scan went to review as "Not checked", it never linked. It now
        // neither links nor goes to review — it waits. "Links on the barcode alone" is reserved for the
        // master switch being off, and must never appear here: two different states, two different fixes.
        detail={
          standDown
            ? 'Set to 0 — nothing is read a second time, and nothing goes through'
            : capped
              ? 'Reached — scans are waiting for the reset, not going to review'
              : 'Scans past this number wait for the reset'
        }
        value={cap}
      />
      {/* Only when it actually happened. A permanent "0 refused" row is noise that trains people to
          skim the card, and this is the row that has to be noticed. */}
      {stats.deniedToday > 0 && (
        <CheckRow
          icon={Ban}
          tone="warning"
          title="Refused — budget spent"
          detail="Second readings the cap turned away; those scans are waiting, not in review"
          value={stats.deniedToday}
        />
      )}
      <CheckRow
        icon={LinkIcon}
        tone="info"
        title="Auto-linked"
        detail="Both readers agreed, nobody looked"
        value={stats.autoLinkedToday}
      />
    </section>
  );
}

function BothBusyExplainer() {
  return (
    <section className="rounded-lg border border-border bg-muted/40 p-3">
      <h3 className="text-sm font-medium text-foreground">What happens when a check doesn’t go through</h3>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
        Nothing fails and nothing is lost. In order:
      </p>
      <ol className="mt-1 list-decimal space-y-0.5 pl-4 text-xs leading-relaxed text-muted-foreground">
        <li>If the first model is busy, the backup model runs instead — usually nobody notices.</li>
        <li>
          If both are busy, the scan shows <em>The check timed out — trying again</em> and waits its
          turn. This is normal, and it never counts against the numbers on this screen.
        </li>
        <li>If the reader is down for everyone, scans wait instead of being tried at all.</li>
        <li>
          Only when every try has failed on one scan does it reach review as <em>Not checked</em>. A
          scan is never linked on its own without a second reading.
        </li>
      </ol>
      {/* 86d3yeh4j — the case the paragraph above structurally cannot cover, because it is not about
          THIS scan. Worth its own sentence: it is the difference between one scan waiting and every
          scan of the afternoon landing in somebody's review queue, which is what used to happen. */}
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
        If the reader is down for <em>everyone</em>, scans stop being tried at all and wait instead —
        one fix clears the lot. Nothing enters the review queue while that is happening, however long
        it lasts, and they go through on their own once it is back.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------- calibration

function CalibrationTab({ stats }: { stats: VerificationStats }) {
  const pct = (n: number, d: number) => (d === 0 ? null : Math.round((n / d) * 100));
  const decoderPct = pct(stats.decoderCorrect, stats.decoderReadsWithTruth);
  const aiPct = pct(stats.aiCorrect, stats.aiReadsWithTruth);
  const spotPct = pct(stats.spotChecksConfirmed, stats.spotChecksResolved);

  return (
    <>
      {/*
        ⚠️ ATTEMPTS, not days. Owner decision: the window is the first N verification ATTEMPTS.
        A calendar framing was actively misleading here — all 54 production scans to date arrived in
        a single afternoon, so "day 2 of 3" could elapse with no data in it at all.

        And it MUST have a finished state. The counter is open-ended while the figures below are not
        windowed at all — `GetStatsAsync` aggregates every attempt ever — so once the target is passed
        the old "Attempt N of 100" wording kept counting and read "Attempt 2051 of 100": 2051% of a
        target, stated as if it were progress. Real data found it; a fresh database shows a tidy
        "12 of 100" and hides it for as long as it takes to scan a hundred documents.
      */}
      <div className="rounded-lg border border-info/30 bg-info/5 px-3 py-2">
        {stats.totalAttempts >= stats.windowTarget ? (
          <>
            <p className="text-sm font-medium text-foreground">Enough checks to judge by</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              The first {stats.windowTarget.toLocaleString()} were the target, and{' '}
              {stats.totalAttempts.toLocaleString()} have run. The figures below cover all of them, not
              just the first {stats.windowTarget.toLocaleString()}.
            </p>
          </>
        ) : (
          <>
            <p className="text-sm font-medium text-foreground">
              Attempt {stats.totalAttempts.toLocaleString()} of {stats.windowTarget.toLocaleString()}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              The window is counted in verification attempts, not days — it advances only when scanning does.
            </p>
          </>
        )}
        {/*
          86d403p4v D6 — say out loud how much of the window is replacements, and only when there are
          any. They are a different measurement living in the same total: on an intake scan the second
          reader is working out WHICH document this is, on a replacement the number is already known
          and it is only confirming it, on a file somebody deliberately chose. Left unstated, a batch
          of replacements moves the figures below and nothing on screen explains the shift.

          Reads 0 for every window opened before this shipped — the replacement path recorded no
          attempts at all, which is a large part of why its failure went unnoticed for two months.
        */}
        {stats.replacementAttempts > 0 && (
          <p className="mt-1 text-xs text-muted-foreground">
            {stats.replacementAttempts.toLocaleString()} of these{' '}
            {stats.replacementAttempts === 1 ? 'was a replacement' : 'were replacements'} — a file
            uploaded onto a document that already had one, where the check confirms a number it was
            given rather than working one out.
          </p>
        )}
      </div>

      <section className="space-y-2">
        <h3 className="text-sm font-medium text-foreground">What happened to each scan</h3>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <Stat label="Both agreed — linked" value={stats.autoLinked} />
          <Stat label="AI suggestion" value={stats.aiSuggestions} />
          <Stat label="Conflicts" value={stats.conflicts} />
          {/* 86d3yeh4z — same name as the chip and the registry filter. Nothing in this label
              contains a banned word, so a grep sweep cannot find it; it has to be changed by hand
              or the grid and the chip end up naming one state two different ways. */}
          <Stat label="Only the barcode reader" value={stats.unconfirmed} />
          <Stat label="Neither could read" value={stats.unreadable} />
          <Stat label="Not checked" value={stats.verifierUnavailable} />
        </div>
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-medium text-foreground">Head to head</h3>
        <p className="text-xs leading-relaxed text-muted-foreground">
          Auto-linked scans are left out on purpose: for those, “correct” is whatever the system
          already decided, so marking them would just be the system grading its own work.{' '}
          {stats.humanResolvedTotal} reads settled by a person so far.
        </p>
        <ReaderBar label="Barcode reader" correct={stats.decoderCorrect} total={stats.decoderReadsWithTruth} pct={decoderPct} />
        <ReaderBar label="Second reader (AI)" correct={stats.aiCorrect} total={stats.aiReadsWithTruth} pct={aiPct} />
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-medium text-foreground">Spot checks</h3>
        <p className="text-xs leading-relaxed text-muted-foreground">
          The only measurement of what agreement is actually worth — everything else agreement produces
          is never seen by a person.
        </p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <Stat label="Sampled" value={stats.spotChecks} />
          <Stat label="Checked" value={stats.spotChecksResolved} />
          <Stat label="Matched" value={spotPct == null ? '—' : `${spotPct}%`} />
        </div>
      </section>

      {/*
        MANDATORY. Hand-matching misfiled 2 of 45 links on the calibration corpus — the same order as
        the effects being measured — so a human resolution is a NOISY label, not ground truth. Without
        this card the numbers above read as settled rates, which is how a 4% human error rate gets
        written up as a machine error rate.
      */}
      <div className="rounded-lg border border-warning/30 bg-warning/10 p-3">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning" aria-hidden />
          <div className="min-w-0">
            {/* ⚠️ 03-second-reader-dialog.spec.ts:194 asserts this TITLE verbatim — do not reword it.
                86d3z2nze — the body dropped "on the calibration corpus" (internal vocabulary, now in
                BANNED_WORDS) and the trailing sentence that only repeated the title. */}
            <p className="text-sm font-medium text-foreground">Read these as directions, not as settled rates</p>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Human resolutions are themselves ~4% wrong when the papers are re-checked afterwards —
              disputed reads are settled by opening the scan, not by majority.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

function ReaderBar({
  label,
  correct,
  total,
  pct,
}: {
  label: string;
  correct: number;
  total: number;
  pct: number | null;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-3 text-xs">
        <span className="text-foreground">{label}</span>
        <span className="tabular-nums text-muted-foreground">
          {total === 0 ? 'no reads yet' : `${correct} of ${total}${pct != null ? ` · ${pct}%` : ''}`}
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-info" style={{ width: `${pct ?? 0}%` }} aria-hidden />
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-lg border border-border bg-muted/40 p-2">
      <div className="text-lg font-semibold tabular-nums text-foreground">{value}</div>
      <div className="text-xs leading-tight text-muted-foreground">{label}</div>
    </div>
  );
}
